import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FullPageSpinner } from '@/components/Skeleton';
import type { Role } from '@/lib/types';

/**
 * FE-0-4 — route guard. Waits for the bootstrap `/auth/me` to settle before
 * deciding: redirecting while `loading` is true would bounce a signed-in user to the
 * login page on every hard refresh.
 */
export function ProtectedRoute({ roles }: { roles?: Role[] }) {
  const { me, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageSpinner label="Checking your session…" />;

  if (!me) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // A password the admin also knows is provisional. Nothing else in the app is
  // reachable until it is replaced — including by typing a URL directly, which is why
  // this lives in the guard rather than only in `landingPath`.
  if (me.user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (roles && !roles.includes(me.user.role)) {
    // Send them somewhere they can actually use, rather than a dead end.
    return <Navigate to={me.landingPath} replace />;
  }

  return <Outlet />;
}

/** Keeps a signed-in user off the login and invite pages. */
export function PublicOnlyRoute() {
  const { me, loading } = useAuth();

  if (loading) return <FullPageSpinner label="Loading…" />;
  if (me) return <Navigate to={me.landingPath} replace />;

  return <Outlet />;
}
